import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Eye, EyeOff, PlusCircle, ArrowUp, ArrowDown, Loader2, ImageUp, X } from "lucide-react";
import { api, mediaUrl, formatApiError } from "@/lib/api";

const EMPTY = { name: "", logo_path: "", logo_thumb_path: "", status: "published", display_order: 0 };

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api.get("/admin/clients").then((r) => setClients(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.logo_path) {
      toast.error("Upload a client logo first");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/clients/${editingId}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/admin/clients", form);
        toast.success("Client added");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const edit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name || "", logo_path: c.logo_path || "", logo_thumb_path: c.logo_thumb_path || "", status: c.status, display_order: c.display_order || 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove "${c.name || "this client"}"?`)) return;
    await api.delete(`/admin/clients/${c.id}`);
    toast.success("Client removed");
    load();
  };

  const toggle = async (c) => {
    await api.put(`/admin/clients/${c.id}`, {
      name: c.name || "", logo_path: c.logo_path || "", logo_thumb_path: c.logo_thumb_path || "",
      status: c.status === "published" ? "hidden" : "published", display_order: c.display_order || 0,
    });
    load();
  };

  const move = async (i, dir) => {
    const ids = clients.map((c) => c.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.put("/admin/clients/reorder", { ids });
    load();
  };

  const uploadLogos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      if (files.length === 1 || editingId) {
        const fd = new FormData();
        fd.append("files", files[0]);
        const r = await api.post("/admin/upload", fd);
        const f = r.data.files[0];
        const derivedName = f.original_filename.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ");
        setForm((prev) => ({
          ...prev,
          logo_path: f.storage_path,
          logo_thumb_path: f.thumb_path || "",
          name: prev.name || derivedName,
        }));
        toast.success("Logo uploaded");
      } else {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        const r = await api.post("/admin/upload", fd);
        let created = 0;
        for (const f of r.data.files) {
          await api.post("/admin/clients", {
            name: f.original_filename.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " "),
            logo_path: f.storage_path,
            logo_thumb_path: f.thumb_path || "",
            status: "published",
            display_order: 0,
          });
          created++;
        }
        toast.success(`${created} client logos added`);
        setForm(EMPTY);
        load();
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl text-charcoal-900 mb-1">Client Work</h1>
      <p className="text-sm text-charcoal-500 mb-8">
        Upload client and brand logos — they appear in the "Brands I worked for" grid on the homepage. Only logos marked Visible are shown.
      </p>

      <form data-testid="client-form" onSubmit={save} className="bg-surface-white border border-charcoal-900/10 rounded-2xl p-6 mb-8 flex flex-col gap-4">
        <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-copper-600">{editingId ? "Edit Client" : "Add Client"}</p>
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            data-testid="client-logo-upload"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-full border border-charcoal-900/20 text-charcoal-700 text-xs font-medium px-4 py-2.5 hover:border-copper-600 hover:text-copper-600 transition-colors"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageUp className="w-3.5 h-3.5" />}
            {form.logo_path ? "Change logo" : "Upload logo(s)"}
          </button>
          <span className="text-xs text-charcoal-400">Select multiple files to add many clients at once</span>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => { uploadLogos(e.target.files); e.target.value = ""; }}
            data-testid="client-logo-file-input"
          />
          {form.logo_path && (
            <>
              <img src={mediaUrl(form.logo_thumb_path || form.logo_path)} alt="Logo preview" className="h-12 w-auto max-w-[130px] object-contain border border-charcoal-900/10 rounded-lg bg-white px-2 py-1" />
              <button
                type="button"
                data-testid="client-logo-remove"
                onClick={() => setForm({ ...form, logo_path: "", logo_thumb_path: "" })}
                aria-label="Remove logo"
                className="text-charcoal-400 hover:text-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}
          <select
            data-testid="client-status-select"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            aria-label="Status"
            className="bg-surface-white border border-charcoal-900/15 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-copper-600"
          >
            <option value="published">Visible</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
        <div className="flex gap-3">
          <button data-testid="client-save-button" type="submit" disabled={saving || uploading} className="inline-flex items-center justify-center gap-2 rounded-full bg-charcoal-900 text-parchment text-sm font-medium px-6 py-3 hover:bg-copper-600 disabled:opacity-60 transition-colors whitespace-nowrap">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
            {editingId ? "Save" : "Add Client"}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="rounded-full border border-charcoal-900/20 text-sm font-medium px-6 py-3 hover:border-copper-600 transition-colors">
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="flex flex-col gap-3" data-testid="admin-clients-list">
        {clients.map((c, i) => (
          <div key={c.id} data-testid={`client-row-${i}`} className="bg-surface-white border border-charcoal-900/10 rounded-xl p-4 flex items-center gap-3">
            <div className="flex flex-col gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="text-charcoal-400 hover:text-copper-600 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => move(i, 1)} disabled={i === clients.length - 1} aria-label="Move down" className="text-charcoal-400 hover:text-copper-600 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
            </div>
            {c.logo_path ? (
              <img src={mediaUrl(c.logo_thumb_path || c.logo_path)} alt={c.name || "Client logo"} className="h-10 w-auto max-w-[110px] object-contain border border-charcoal-900/10 rounded-lg bg-white px-1.5 py-1 shrink-0" />
            ) : (
              <span className="h-10 px-3 rounded-lg border border-dashed border-charcoal-900/20 flex items-center font-mono text-[9px] text-charcoal-400 shrink-0">NO LOGO</span>
            )}
            <p className="flex-1 font-semibold text-sm text-charcoal-900 truncate">{c.name || "Client"}</p>
            <span className={`hidden sm:inline-block font-mono text-[10px] uppercase border rounded-full px-2.5 py-1 ${c.status === "published" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-charcoal-900/5 text-charcoal-500 border-charcoal-900/15"}`}>{c.status === "published" ? "visible" : "hidden"}</span>
            <button data-testid={`client-toggle-${i}`} onClick={() => toggle(c)} aria-label="Toggle visibility" className="p-2 text-charcoal-400 hover:text-copper-600">{c.status === "published" ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
            <button data-testid={`client-edit-${i}`} onClick={() => edit(c)} aria-label="Edit" className="p-2 text-charcoal-400 hover:text-copper-600"><Pencil className="w-4 h-4" /></button>
            <button data-testid={`client-delete-${i}`} onClick={() => remove(c)} aria-label="Delete" className="p-2 text-charcoal-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
